import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { approveAdvance, createAdvance, disburseAdvance, fetchAdvances, fetchEmployees, rejectAdvance, repayAdvance } from "../../api/HrAPIs";
import { Empty, Field, HrFrame, HrGuard, StatusPill, inputClass, labelOf, money, useHr } from "./hrUi";

export default function AdvancesPage() {
  return <HrGuard anyOf={["HR.ViewAdvances", "HR.ManageAdvances", "HR.ApproveAdvances"]}><Body /></HrGuard>;
}

function Body() {
  const hr = useHr();
  const queryClient = useQueryClient();
  const advances = useQuery({ queryKey: ["hrAdvances"], queryFn: () => fetchAdvances({}) });
  const employees = useQuery({ queryKey: ["hrEmployees", "advances"], queryFn: () => fetchEmployees({ pageSize: 100 }), enabled: hr.can("HR.ManageAdvances") });
  const [form, setForm] = useState({ employeeId: "", requestedAmount: "", reason: "", repaymentMethod: "NextSalary", installmentAmount: "" });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["hrAdvances"] });
  const run = async (work) => { try { await work(); toast.success("تم"); refresh(); } catch (error) { toast.error(error.message); } };

  return (
    <HrFrame title="السلف" subtitle="لا يُخصم شيء قبل تسجيل الصرف الفعلي.">
      {(hr.can("HR.ManageAdvances") || hr.can("HR.ViewAdvances")) && (
        <form className="mb-4 grid gap-2 rounded-2xl border bg-white p-4 md:grid-cols-2" onSubmit={(event) => { event.preventDefault(); run(() => createAdvance({ ...form, employeeId: Number(form.employeeId || hr.data?.employeeId), requestedAmount: Number(form.requestedAmount), installmentAmount: form.installmentAmount ? Number(form.installmentAmount) : null })); }}>
          {hr.can("HR.ManageAdvances") && (
            <Field label="الموظف">
              <select className={inputClass} value={form.employeeId} onChange={(event) => setForm({ ...form, employeeId: event.target.value })}>
                <option value="">أنا</option>
                {(employees.data?.items || []).map((item) => <option key={item.id} value={item.id}>{item.fullName}</option>)}
              </select>
            </Field>
          )}
          <Field label="المبلغ"><input className={inputClass} required type="number" step="0.01" value={form.requestedAmount} onChange={(event) => setForm({ ...form, requestedAmount: event.target.value })} /></Field>
          <Field label="السداد">
            <select className={inputClass} value={form.repaymentMethod} onChange={(event) => setForm({ ...form, repaymentMethod: event.target.value })}>
              {["NextSalary", "Installments", "FixedMonthly", "Manual"].map((method) => <option key={method} value={method}>{labelOf(method)}</option>)}
            </select>
          </Field>
          <Field label="القسط"><input className={inputClass} type="number" step="0.01" value={form.installmentAmount} onChange={(event) => setForm({ ...form, installmentAmount: event.target.value })} /></Field>
          <Field label="السبب"><input className={inputClass} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} /></Field>
          <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white">طلب سلفة</button>
        </form>
      )}
      {(advances.data || []).length === 0 && <Empty text="لا توجد سلف." />}
      <div className="grid gap-3">
        {(advances.data || []).map((row) => (
          <article key={row.id} className="rounded-2xl border bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="font-bold">{row.employeeName}</p>
              <StatusPill value={row.status} />
            </div>
            <p className="text-sm text-slate-600">مطلوب {money(row.requestedAmount)} · مصروف {money(row.disbursementAmount)} · متبقي {money(row.remainingBalance)} · {labelOf(row.repaymentMethod)}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-sm">
              {row.status === "Pending" && hr.can("HR.ApproveAdvances") && <button type="button" onClick={() => run(() => approveAdvance(row.id))}>اعتماد</button>}
              {row.status === "Pending" && hr.can("HR.ApproveAdvances") && <button type="button" onClick={() => run(() => rejectAdvance(row.id))}>رفض</button>}
              {row.status === "Approved" && hr.can("HR.ManageAdvances") && <button type="button" onClick={() => run(() => disburseAdvance(row.id, {}))}>تسجيل الصرف</button>}
              {row.status === "Disbursed" && hr.can("HR.ManageAdvances") && <button type="button" onClick={() => { const amount = window.prompt("مبلغ السداد اليدوي"); if (amount) run(() => repayAdvance(row.id, { amount: Number(amount) })); }}>سداد يدوي</button>}
            </div>
          </article>
        ))}
      </div>
    </HrFrame>
  );
}
